/**
 * ResourceRequestDrawer
 *
 * Side drawer for raising a resource request against a project. Sends the
 * request to the connected scheduler (e.g. Access Planner).
 *
 * Phase 1: Uses mock data for role options, skills taxonomy and scheduler name.
 * TODO: Wire to POST /api/projects/{id}/scheduling/requests when backend is ready.
 */

import { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, Loader2, Search, X, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import {
  mockRoleCoverage,
  mockSchedulerConnection,
} from '@/lib/mock-scheduling-data';
import { MOCK_SKILLS, SKILL_CATEGORIES } from '@/lib/mock-skills-data';
import { createResourceRequest } from '@/services/schedulingService';

// ─── Types ────────────────────────────────────────────────────────────────────

type Priority = 'nice-to-have' | 'important' | 'critical';

type SkillRequirement = 'required' | 'preferred';

interface SelectedSkill {
  skillId: string;
  skillName: string;
  category: string;
  requirement: SkillRequirement;
}

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'nice-to-have', label: 'Nice to Have' },
  { value: 'important', label: 'Important' },
  { value: 'critical', label: 'Critical' },
];

const MAX_SKILLS = 8;

// ─── Category badge colours ───────────────────────────────────────────────────

const CATEGORY_COLOURS: Record<string, string> = {
  Technical:        'bg-blue-100 text-blue-700 border-blue-200',
  Design:           'bg-purple-100 text-purple-700 border-purple-200',
  Delivery:         'bg-teal-100 text-teal-700 border-teal-200',
  'Domain / Sector':'bg-amber-100 text-amber-700 border-amber-200',
  Leadership:       'bg-rose-100 text-rose-700 border-rose-200',
};

// ─── Skills Picker ────────────────────────────────────────────────────────────

interface SkillsPickerProps {
  selectedSkills: SelectedSkill[];
  onToggleSkill: (skillId: string, skillName: string, category: string) => void;
  onRemoveSkill: (skillId: string) => void;
  onChangeRequirement: (skillId: string, req: SkillRequirement) => void;
}

function SkillsPicker({
  selectedSkills,
  onToggleSkill,
  onRemoveSkill,
  onChangeRequirement,
}: SkillsPickerProps) {
  const [search, setSearch]           = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const selectedIds = new Set(selectedSkills.map((s) => s.skillId));
  const atMax       = selectedSkills.length >= MAX_SKILLS;

  // Filtered flat list
  const visibleSkills = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MOCK_SKILLS.filter((s) => {
      const matchesSearch   = !q || s.skillName.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
      const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [search, categoryFilter]);

  return (
    <div className="space-y-3">

      {/* Toolbar: search + category filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills…"
            className="pl-8 h-8 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none z-10" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-8 text-sm pl-8 w-[160px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {SKILL_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Skills table */}
      <div className="border rounded-md overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1.5rem_1fr_auto_auto] gap-x-3 items-center px-3 py-2 bg-muted/50 border-b">
          <span />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Skill</span>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide w-[90px] text-center">Category</span>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide w-[140px] text-center">Requirement</span>
        </div>

        {/* Rows */}
        <div className="divide-y max-h-[260px] overflow-y-auto">
          {visibleSkills.length === 0 && (
            <p className="px-3 py-4 text-sm text-muted-foreground text-center">
              No skills match your search.
            </p>
          )}
          {visibleSkills.map((skill) => {
            const isSelected = selectedIds.has(skill.skillId);
            const disabled   = !isSelected && atMax;
            const selEntry   = selectedSkills.find((s) => s.skillId === skill.skillId);

            return (
              <div
                key={skill.skillId}
                className={cn(
                  'grid grid-cols-[1.5rem_1fr_auto_auto] gap-x-3 items-center px-3 py-2.5 transition-colors',
                  isSelected ? 'bg-primary/5' : 'hover:bg-muted/30',
                  disabled && 'opacity-40',
                )}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onToggleSkill(skill.skillId, skill.skillName, skill.category)}
                  className="h-4 w-4 rounded accent-primary cursor-pointer disabled:cursor-not-allowed"
                />

                {/* Skill name */}
                <span className="text-sm font-medium truncate">{skill.skillName}</span>

                {/* Category badge */}
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border w-[90px] justify-center',
                    CATEGORY_COLOURS[skill.category] ?? 'bg-muted text-foreground border-border',
                  )}
                >
                  {skill.category === 'Domain / Sector' ? 'Domain' : skill.category}
                </span>

                {/* Required / Preferred — only visible when selected */}
                <div className="w-[140px] flex items-center justify-center">
                  {isSelected && selEntry ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`req-${skill.skillId}`}
                          value="required"
                          checked={selEntry.requirement === 'required'}
                          onChange={() => onChangeRequirement(skill.skillId, 'required')}
                          className="accent-primary h-3.5 w-3.5"
                        />
                        <span className="text-xs">Required</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name={`req-${skill.skillId}`}
                          value="preferred"
                          checked={selEntry.requirement === 'preferred'}
                          onChange={() => onChangeRequirement(skill.skillId, 'preferred')}
                          className="accent-primary h-3.5 w-3.5"
                        />
                        <span className="text-xs">Preferred</span>
                      </label>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer: selection count */}
        <div className={cn(
          'px-3 py-2 border-t flex items-center justify-between',
          atMax ? 'bg-amber-50' : 'bg-muted/30',
        )}>
          <span className="text-xs text-muted-foreground">
            {visibleSkills.length} skill{visibleSkills.length !== 1 ? 's' : ''} shown
          </span>
          <span className={cn('text-xs font-medium', atMax ? 'text-amber-600' : 'text-muted-foreground')}>
            {selectedSkills.length} / {MAX_SKILLS} selected
            {atMax && ' — limit reached'}
          </span>
        </div>
      </div>

      {/* Selected skill chips — quick-remove row */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedSkills.map((s) => (
            <Badge
              key={s.skillId}
              variant="secondary"
              className="gap-1 pr-1 text-xs font-normal"
            >
              {s.skillName}
              <span className={cn(
                'ml-0.5 text-[10px] px-1 rounded-sm font-medium',
                s.requirement === 'required'
                  ? 'bg-primary/20 text-primary'
                  : 'bg-muted text-muted-foreground',
              )}>
                {s.requirement === 'required' ? 'Req' : 'Pref'}
              </span>
              <button
                type="button"
                onClick={() => onRemoveSkill(s.skillId)}
                className="hover:text-destructive ml-0.5 rounded-sm"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface ResourceRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fills the role and locks it when opened from a specific budget row */
  prefilledRole?: string;
  projectStartDate?: string;
  projectEndDate?: string;
  /** Displayed in the drawer sub-header (e.g. "Alpha Replatform") */
  projectName?: string;
  /** Project code displayed alongside project name (e.g. "P-001") */
  projectCode?: string;
  /** Project ID used to call the scheduling API */
  projectId?: string;
  /** Called with the submitted role name after a successful submission */
  onSubmitSuccess?: (roleName: string) => void;
}

export function ResourceRequestModal({
  open,
  onOpenChange,
  prefilledRole,
  projectStartDate,
  projectName,
  projectCode,
  projectId,
  onSubmitSuccess,
}: ResourceRequestModalProps) {
  // When a role is pre-filled from a budget row it is locked (read-only)
  const isRoleLocked = Boolean(prefilledRole);

  // ── Form state ───────────────────────────────────────────────────────────────
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [hoursRequired, setHoursRequired] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(
    projectStartDate ? new Date(projectStartDate) : undefined,
  );
  const [priority, setPriority] = useState<Priority>('important');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Skills state ─────────────────────────────────────────────────────────────
  const [selectedSkills, setSelectedSkills] = useState<SelectedSkill[]>([]);

  // Pre-fill role when opened from a specific row
  useEffect(() => {
    if (open) {
      const matchedRole = prefilledRole
        ? (mockRoleCoverage.find(
            (r) =>
              r.roleName.toLowerCase() === prefilledRole.toLowerCase() ||
              r.roleName.toLowerCase().includes(prefilledRole.toLowerCase()) ||
              prefilledRole.toLowerCase().includes(r.roleName.toLowerCase()),
          )?.roleName ?? prefilledRole)
        : '';

      setSelectedRole(matchedRole);

      if (matchedRole) {
        const roleData = mockRoleCoverage.find((r) => r.roleName === matchedRole);
        if (roleData) {
          const gap = roleData.budgetHours - roleData.scheduledHours;
          setHoursRequired(gap > 0 ? String(gap) : '');
        }
      } else {
        setHoursRequired('');
      }

      setStartDate(projectStartDate ? new Date(projectStartDate) : undefined);
      setPriority('important');
      setNotes('');
      setSelectedSkills([]);
      setSubmitAttempted(false);
    }
  }, [open, prefilledRole, projectStartDate]);

  // Auto-update suggested hours when role changes
  const handleRoleChange = (roleName: string) => {
    setSelectedRole(roleName);
    const roleData = mockRoleCoverage.find((r) => r.roleName === roleName);
    if (roleData) {
      const gap = roleData.budgetHours - roleData.scheduledHours;
      setHoursRequired(gap > 0 ? String(gap) : '');
    } else {
      setHoursRequired('');
    }
  };

  // ── Skills handlers ───────────────────────────────────────────────────────────
  const handleToggleSkill = (skillId: string, skillName: string, category: string) => {
    setSelectedSkills((prev) => {
      const exists = prev.find((s) => s.skillId === skillId);
      if (exists) return prev.filter((s) => s.skillId !== skillId);
      if (prev.length >= MAX_SKILLS) return prev;
      return [...prev, { skillId, skillName, category, requirement: 'preferred' }];
    });
  };

  const handleRemoveSkill = (skillId: string) =>
    setSelectedSkills((prev) => prev.filter((s) => s.skillId !== skillId));

  const handleChangeRequirement = (skillId: string, req: SkillRequirement) =>
    setSelectedSkills((prev) =>
      prev.map((s) => (s.skillId === skillId ? { ...s, requirement: req } : s)),
    );

  const selectedRoleData = mockRoleCoverage.find((r) => r.roleName === selectedRole);

  const isValid =
    selectedRole.trim() !== '' &&
    hoursRequired.trim() !== '' &&
    Number(hoursRequired) > 0 &&
    startDate !== undefined;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const response = await createResourceRequest(projectId!, {
        roleId: selectedRoleData?.roleName,
        roleName: selectedRole,
        hoursRequired: Number(hoursRequired),
        desiredStartDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        priority,
        notes: notes || undefined,
      });

      if (response.status === 'Pending') {
        toast.success(
          `Resource request raised for ${selectedRole}. The resourcing team will be notified via ${mockSchedulerConnection.displayName}.`,
        );
        onSubmitSuccess?.(selectedRole);
        onOpenChange(false);
      } else {
        toast.error(response.message || 'Failed to create resource request.');
      }
    } catch (error) {
      console.error('[ResourceRequestDrawer] Submit error:', error);
      toast.error('Failed to create resource request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[680px] flex flex-col overflow-hidden p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle>Raise Resource Request</SheetTitle>
          <SheetDescription>
            {projectName ? (
              <span className="font-medium text-foreground">
                {projectCode ? (
                  <>
                    <span className="text-muted-foreground">{projectCode}</span>
                    {' · '}
                    {projectName}
                  </>
                ) : (
                  projectName
                )}
              </span>
            ) : (
              'Submit a resource request to the resourcing team.'
            )}
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Role — read-only when opened from a budget row, editable from "New Request" */}
          <div className="space-y-2">
            <Label htmlFor="role">
              Role <span className="text-destructive">*</span>
            </Label>
            {isRoleLocked ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/50 text-sm">
                <span className="font-medium">{selectedRole}</span>
                <span className="ml-auto text-xs text-muted-foreground">Locked to budget line</span>
              </div>
            ) : (
              <>
                <Select value={selectedRole} onValueChange={handleRoleChange}>
                  <SelectTrigger
                    id="role"
                    className={cn(submitAttempted && !selectedRole && 'border-destructive')}
                  >
                    <SelectValue placeholder="Select a role…" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockRoleCoverage.map((r) => (
                      <SelectItem key={r.roleName} value={r.roleName}>
                        {r.roleName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {submitAttempted && !selectedRole && (
                  <p className="text-xs text-destructive">Please select a role.</p>
                )}
              </>
            )}
            {/* Role context: budget vs scheduled */}
            {selectedRoleData && (
              <p className="text-xs text-muted-foreground">
                Budget:{' '}
                <span className="font-medium">{selectedRoleData.budgetHours}h</span>
                &nbsp;·&nbsp;Scheduled:{' '}
                <span className="font-medium">{selectedRoleData.scheduledHours}h</span>
                &nbsp;·&nbsp;Gap:{' '}
                <span
                  className={cn(
                    'font-medium',
                    selectedRoleData.budgetHours - selectedRoleData.scheduledHours > 0
                      ? 'text-amber-600'
                      : 'text-green-600',
                  )}
                >
                  {selectedRoleData.budgetHours - selectedRoleData.scheduledHours}h
                </span>
              </p>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="border-t" />

          {/* Hours Required */}
          <div className="space-y-2">
            <Label htmlFor="hours">
              Hours Required <span className="text-destructive">*</span>
            </Label>
            <Input
              id="hours"
              type="number"
              min={1}
              placeholder="e.g. 120"
              value={hoursRequired}
              onChange={(e) => setHoursRequired(e.target.value)}
              className={cn(
                submitAttempted &&
                  (hoursRequired.trim() === '' || Number(hoursRequired) <= 0) &&
                  'border-destructive',
              )}
            />
            {submitAttempted && hoursRequired.trim() === '' && (
              <p className="text-xs text-destructive">Please enter the hours required.</p>
            )}
            {submitAttempted &&
              hoursRequired.trim() !== '' &&
              Number(hoursRequired) <= 0 && (
                <p className="text-xs text-destructive">Hours must be greater than zero.</p>
              )}
          </div>

          {/* Desired Start Date */}
          <div className="space-y-2">
            <Label>
              Desired Start Date <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              When do you need this resource to start? The resourcing team will use the
              project budget breakdown to confirm the full booking pattern in Access Planner.
            </p>
            <Popover open={startOpen} onOpenChange={setStartOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !startDate && 'text-muted-foreground',
                    submitAttempted && !startDate && 'border-destructive',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'dd MMM yyyy') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(d) => {
                    setStartDate(d);
                    setStartOpen(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {submitAttempted && !startDate && (
              <p className="text-xs text-destructive">Please select a desired start date.</p>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select
              value={priority}
              onValueChange={(v) => setPriority(v as Priority)}
            >
              <SelectTrigger id="priority">
                <SelectValue placeholder="Select priority…" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Divider ── */}
          <div className="border-t" />

          {/* Skills — optional section */}
          <div className="space-y-2">
            <div>
              <Label className="text-sm font-medium">
                Skills Required{' '}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Help the resourcing team find the best match in{' '}
                {mockSchedulerConnection.displayName}. Select up to {MAX_SKILLS} skills and
                mark each as Required or Preferred.
              </p>
            </div>
            <SkillsPicker
              selectedSkills={selectedSkills}
              onToggleSkill={handleToggleSkill}
              onRemoveSkill={handleRemoveSkill}
              onChangeRequirement={handleChangeRequirement}
            />
          </div>

          {/* ── Divider ── */}
          <div className="border-t" />

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Describe the demand pattern for the resourcing team — e.g. 'Needed heavily at project start, lighter through mid-delivery, then back for go-live in Q4.'"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The resourcing team will reference the monthly budget breakdown in Access Planner
              to confirm exact hours per month.
            </p>
          </div>
        </div>

        {/* Footer — pinned to bottom */}
        <div className="px-6 py-4 border-t shrink-0 flex justify-end gap-3 bg-background">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
